import { useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { TransportEquipmentCountStatus } from "__graphql__/types";
import { EquipmentCard } from "flows/Activities/components/TransportEquipment/EquipmentCard";
import {
  useGetActiveEquipmentTypesForSupplierQuery,
  useGetTransportEquipmentCountsQuery,
} from "flows/Activities/queries/deliveryCheckin/transportEquipments.generated";
import { useDeliveryStore } from "flows/Activities/stores/useDeliveryStore";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Spinner } from "ui/Spinner/Spinner";
import { TitleM } from "ui/Typography/Typography";

type CountsState = {
  [equipmentTypeId: string]: { preparing: number };
};

export type PrecountEquipmentInfo = {
  lineItems: { equipmentTypeId: string; preparing: number }[];
  transportEquipmentCountId?: string;
};

type PrecountEquipmentListProps = {
  onCountsChanged: (info: PrecountEquipmentInfo) => void;
};

export function PrecountEquipmentList({ onCountsChanged }: PrecountEquipmentListProps) {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();

  const { externalSupplierId } = useDeliveryStore(
    useShallow((state) => ({
      externalSupplierId: state.externalSupplierId,
    })),
  );

  const [countsState, setCountsState] = useState<CountsState>({});

  const { data: activeEquipmentTypes, loading: isLoadingActiveEquipmentTypes } =
    useGetActiveEquipmentTypesForSupplierQuery({
      variables: {
        externalSupplierId: externalSupplierId!,
      },
      onError: () => {
        showToastUI({
          title: "components.activities.transport-equipment.count-page.get-equipment.error.title",
        });
      },
      fetchPolicy: "network-only",
      skip: !externalSupplierId,
    });

  const { data: pendingCountsData } = useGetTransportEquipmentCountsQuery({
    variables: {
      externalSupplierId: externalSupplierId!,
      status: TransportEquipmentCountStatus.pending,
    },
    skip: !externalSupplierId,
  });

  const pendingCount =
    pendingCountsData?.getTransportEquipmentCounts.transportEquipmentCounts[0] ?? null;

  useEffect(() => {
    // Wait for both queries to resolve before seeding initial counts
    if (!activeEquipmentTypes || pendingCountsData === undefined) return;

    const initialCounts: CountsState = {};
    activeEquipmentTypes.getActiveEquipmentTypesForSupplier.equipmentTypes.forEach((item) => {
      const existingCount = pendingCount?.lineItems.find(
        (lineItem) => lineItem.equipmentTypeId === item.id,
      );
      initialCounts[item.id] = {
        preparing: existingCount?.returned ?? 0,
      };
    });
    setCountsState(initialCounts);
  }, [activeEquipmentTypes, pendingCountsData, pendingCount]);

  useEffect(() => {
    if (!activeEquipmentTypes) return;

    const lineItems = activeEquipmentTypes.getActiveEquipmentTypesForSupplier.equipmentTypes.map(
      (item) => ({
        equipmentTypeId: item.id,
        preparing: countsState[item.id]?.preparing ?? 0,
      }),
    );

    onCountsChanged({ lineItems, transportEquipmentCountId: pendingCount?.id });
  }, [countsState, activeEquipmentTypes, onCountsChanged, pendingCount?.id]);

  const updateCount = (equipmentTypeId: string, field: "preparing", value: number) => {
    setCountsState((prev) => ({
      ...prev,
      [equipmentTypeId]: {
        ...prev[equipmentTypeId],
        [field]: value,
      },
    }));
  };

  const equipment =
    activeEquipmentTypes?.getActiveEquipmentTypesForSupplier.equipmentTypes.map((item) => {
      const counters = [
        {
          label: intl.formatMessage({
            id: "components.activities.transport-equipment.precount-page.count.preparing",
          }),
          count: countsState[item.id]?.preparing ?? 0,
          updateHandler: (count: number) => updateCount(item.id, "preparing", count),
        },
      ];

      return {
        id: item.id,
        name: item.name,
        photoUrl: item.photoUrl,
        depositInEuros: item.depositInEuros,
        counters,
      };
    }) || [];

  return (
    <>
      <TitleM pt="s300" px="s200" pb="s200">
        {intl.formatMessage({
          id: "components.activities.transport-equipment.count-page.count.title",
        })}
      </TitleM>
      <Box bg="white" borderRadius="sm" p="s200">
        {isLoadingActiveEquipmentTypes ? (
          <Spinner display="block" margin="0 auto" />
        ) : (
          equipment.map((item) => (
            <EquipmentCard
              key={item.id}
              name={item.name}
              photoUrl={item.photoUrl}
              depositInEuros={item.depositInEuros}
              counters={item.counters}
            />
          ))
        )}
      </Box>
    </>
  );
}
